/** The browser's half of the document sync. Four calls, because the API only offers four.
 *
 * ⚠⚠ **THIS IS NOT `chrome/api.ts` AND THE DIFFERENCE MATTERS.** That file is the OLD app's
 * 25-endpoint surface, every one of which throws on purpose so an unbuilt feature cannot be
 * mistaken for a working one. This file is what pdcad's own backend actually answers. When an
 * endpoint here grows, a stub there gets deleted — never the reverse.
 *
 * ⚠ There is deliberately no `saveEntity`. `backend/drawing/views.py` explains why at length:
 * an edit that needs a round trip cannot feel like AutoCAD, so editing will be a bulk save of
 * what the browser changed, not a call per object.
 *
 * ⚠⚠ **AND SOME OF THESE NEVER REACH THE NETWORK — phase 3.** A drawing open in the browser
 * engine (`local/registry.ts`) answers its own reads and its own edits, out of `pdcad-core`
 * compiled to wasm, and the functions below ask the registry BEFORE they fetch. That decision
 * lives here and nowhere else on purpose: `Studio.tsx` calls `loadDisplay` and `editDrawing`
 * from dozens of places, and routing at the call sites would be dozens of chances to miss one —
 * with the missed one quietly asking the server about a drawing it has never seen.
 */
import type { DisplayList } from "./Canvas2D";
import { LocalDrawing } from "./local/adapter";
import { localFor } from "./local/registry";
import type { TextSpec } from "./TextEditor";

/** ⚠ The field names are the API's, not prettier ones. `entities` is a COUNT here and a list in
 *  the detail serializer; renaming it in this file would hide that from anyone reading both. */
export interface DrawingRow {
  id: number;
  name: string;
  source_ext: string;
  source_name: string;
  dxfversion: string;
  entities: number;
  layer_count: number;
  created_at: string;
  updated_at?: string;
  /** The space to open in — AutoCAD's TILEMODE, resolved server-side to a NAME.
   *  ⚠ `"Model"` or a layout's name, never a boolean: TILEMODE says only WHETHER the drawing
   *  was on a sheet, and which sheet is a question only the server's layout list can answer. */
  opens_in?: string;
  /** ⚠ Hidden from the list unless it is asked for. Archived is not deleted. */
  archived?: boolean;
}

/** The CSRF token Django put in a cookie, for the `X-CSRFToken` header.
 *
 * ⚠⚠ **EVERY UNSAFE REQUEST NEEDS THIS OR IT IS A 403, AND THE 403 READS LIKE A BUG.** Session
 * auth means the browser attaches the cookie by itself, which is exactly why another site could
 * make it do so — the token is the proof that the request came from a page served by this
 * origin. `auth.Me` is decorated `ensure_csrf_cookie` so the cookie is there from the first
 * load; this app renders no Django templates, so nothing else would ever set it.
 *
 * ⚠ Read fresh on every call, never captured: `login()` CYCLES the session and Django issues a
 * new CSRF token with it, so a token read once at start-up is stale the moment anyone logs in.
 */
/** Django's CSRF header, from the cookie. ⚠ Exported for `local/open.ts`, whose `saveToServer`
 *  posts to the API without going through the helpers below — duplicating it there would be a
 *  second place to get a security header right. */
export function csrf(): Record<string, string> {
  const m = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]*)/);
  return m ? { "X-CSRFToken": decodeURIComponent(m[1]) } : {};
}

/** Thrown when the server says "log in first". ⚠ Its own type so the shell can show the login
 *  screen instead of a red toast that a drafter cannot act on. */
export class NotLoggedIn extends Error {
  constructor() { super("Your session has expired. Please sign in again."); }
}

async function get<T>(url: string): Promise<T> {
  const r = await fetch(url, { headers: { Accept: "application/json" } });
  if (r.status === 401 || r.status === 403) throw new NotLoggedIn();
  if (!r.ok) {
    // ⚠ The server's own words. "Request failed" is not something anyone can act on, and DRF
    // puts the useful sentence in `detail`.
    let why = `${r.status} ${r.statusText}`;
    try {
      const j = await r.json();
      if (j?.detail) why = String(j.detail);
    } catch { /* a non-JSON error body is still an error */ }
    throw new Error(why);
  }
  return r.json() as Promise<T>;
}

export async function listDrawings(q = ""): Promise<DrawingRow[]> {
  const j = await get<any>(`/api/drawings/${q}`);
  // ⚠ DRF pages by default and un-pages when it is configured not to. Accept both rather than
  // depending on a setting three files away.
  return Array.isArray(j) ? j : (j.results ?? []);
}

/** A space of the drawing. ⚠ `model` is the flag, not the name: AutoCAD's model space is called
 *  "Model" in English and something else in a drawing saved from a localised install. */
export interface LayoutRow { name: string; model: boolean; seq: number; entities: number }

/** A dimension style as the drawing holds it. ⚠ `used` is how many DIMENSIONs name it — a
 *  style used 40 times is the office standard, one used 0 times is import debris. */
export interface DimStyleRow {
  name: string;
  used: number;
  vars: Record<string, number | string>;
}

export function loadDimStyles(id: number): Promise<{
  styles: DimStyleRow[]; vars: Record<string, string>; viewport_scales: number[];
}> {
  const d = localFor(id);
  if (d) return d.dimStyles() as Promise<never>;
  return get(`/api/drawings/${id}/dimstyles/`);
}

/** A TEXT style — a DXF symbol-table row, not the design app's pen style.
 *  ⚠ `css`, `weight` and `italic` are what the CANVAS resolved the font FILE to, which is a
 *  different thing from the file the style names whenever the machine does not have it. That
 *  difference is the most useful thing the manager can show. See `backend/drawing/textstyles.py`. */
export interface TextStyleRow {
  name: string; font: string; bigfont: string;
  height: number; width: number; oblique: number;
  css: string; weight: number; italic: boolean; used: number;
}

export function loadTextStyles(id: number): Promise<{
  styles: TextStyleRow[]; fields: Record<string, string>;
}> {
  const d = localFor(id);
  if (d) return d.textStyles() as Promise<never>;
  return get(`/api/drawings/${id}/textstyles/`);
}

/** A FIELD — text AutoCAD recomputes. ⚠ `value` is `null` when this app cannot know: the
 *  `View*` family is AutoCAD's section/detail documentation and pdcad does not model it, so
 *  those are reported as carried rather than guessed at. See `backend/drawing/fields.py`. */
export interface FieldRow {
  handle: string; owner: string; kind: string; layout: string;
  code: string; var: string; value: string | null; shows: string;
  stale: boolean; reads: string;
}

export function loadFields(id: number): Promise<{
  fields: FieldRow[]; evaluable: number; stale: number;
  carried: Record<string, number>; why: Record<string, string>;
}> {
  const d = localFor(id);
  if (d) return d.fields() as Promise<never>;
  return get(`/api/drawings/${id}/fields/`);
}

/** An external reference. ⚠ `found` is a PATH or null, and null is the normal answer — every
 *  path in this office's archive is absolute and names another machine. `entities` > 0 means it
 *  has been bound and the geometry is in this file. */
export interface XrefRow {
  name: string; path: string; file: string; overlay: boolean;
  found: string | null; inserts: number; entities: number;
}
export interface XrefTarget { id: number; name: string; entities: number }

export function loadXrefs(id: number): Promise<{ xrefs: XrefRow[]; drawings: XrefTarget[] }> {
  return get(`/api/drawings/${id}/xrefs/`);
}

export function loadLayouts(id: number): Promise<{ layouts: LayoutRow[] }> {
  const d = localFor(id);
  if (d) return d.layouts() as Promise<never>;
  return get<{ layouts: LayoutRow[] }>(`/api/drawings/${id}/layouts/`);
}

export function loadDisplay(id: number, space?: string): Promise<DisplayList> {
  const d = localFor(id);
  if (d) return d.display(space || "Model") as Promise<DisplayList>;
  const q = space ? `?space=${encodeURIComponent(space)}` : "";
  return get<DisplayList>(`/api/drawings/${id}/display/${q}`);
}

/** A layer as the DOCUMENT holds it. ⚠ Not the same thing as `DisplayLayer`, which is the
 *  drawing's render state and carries no id — see `patchLayer` in `Studio.tsx`. */
export interface LayerRow {
  id: number;
  name: string;
  aci: number;
  true_colour: number | null;
  linetype: string;
  /** ⚠ An INT in 1/100 mm. -1 ByLayer, -2 ByBlock, -3 Default. See `models.Layer`. */
  lineweight: number;
  on: boolean;
  frozen: boolean;
  locked: boolean;
  plot: boolean;
}

export function loadLayers(id: number): Promise<LayerRow[]> {
  const d = localFor(id);
  if (d) return d.layers() as Promise<never>;
  return get<LayerRow[]>(`/api/drawings/${id}/layers/`);
}

/** ⚠ PATCH, not PUT: the serializer's `name` is read-only and a PUT would have to send it.
 *  Renaming a layer means renaming every reference to it and is its own command. */
export async function patchLayerRow(layerId: number,
                                    patch: Partial<LayerRow>): Promise<LayerRow> {
  const r = await fetch(`/api/layers/${layerId}/`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", Accept: "application/json", ...csrf() },
    body: JSON.stringify(patch),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j?.detail || JSON.stringify(j) || `${r.status} ${r.statusText}`);
  return j as LayerRow;
}

/** ⚠ Returns the REPORT as well as the drawing — `lost` is not decoration. A file that imported
 *  with losses must say so at the moment it happens, not when someone opens it next month. */
export async function importFile(f: File): Promise<{ drawing: any; report: any }> {
  const fd = new FormData();
  fd.append("file", f);
  // ⚠ NO `Content-Type` here — `fetch` sets `multipart/form-data` WITH ITS BOUNDARY when the
  // body is a FormData, and naming it by hand omits the boundary and the upload arrives empty.
  const r = await fetch("/api/drawings/import_file/",
                        { method: "POST", body: fd, headers: csrf() });
  if (r.status === 401 || r.status === 403) throw new NotLoggedIn();
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j?.detail ? `${j.detail}${j.converter ? `: ${j.converter}` : ""}`
                                       : `${r.status} ${r.statusText}`);
  return j;
}

/** What `/edit/` accepts. ⚠ ONE request per command, not per object — see `views.edit`. */
export type EditOp = "move" | "copy" | "erase" | "rotate" | "scale" | "mirror" | "offset"
                   | "draw" | "grip" | "trim" | "extend" | "fillet" | "chamfer"
                   | "hatch" | "hatchedit" | "block" | "insert" | "explode" | "renameblock"
                   // ⚠ Paper space. `mview` and `vpscale` act on a LAYOUT, not on a selection
                   // of geometry — see `edits.NEEDS_SELECTION`, which is where that rule lives.
                   | "newlayout" | "dellayout" | "renamelayout" | "mview" | "vpscale"
                   // ⚠⚠ These two act on ONE WINDOW rather than on the sheet. VPLAYER freezes a
                   // layer in this viewport only — `LAYFRZ` on a sheet set takes it off every
                   // sheet at once — and VPLOCK is what stops a stray zoom changing a finished
                   // sheet's plot scale. 254 of the 1,803 viewports stored here freeze a layer;
                   // 311 are locked.
                   | "vplayer" | "vplock"
                   // ⚠ CIRCLE's `turn Arc into circle` — a CONVERSION, so it acts on the
                   // selection like every other one and returns the new handles.
                   | "arc2circle"
                   // ⚠ REVCLOUD's and SPLINE's `Entity` — conversions, like `arc2circle`.
                   | "revcloudentity" | "splineentity"
                   // ⚠ OVERKILL judges the WHOLE drawing when nothing is selected — the only op
                   // here for which an empty `handles` is a meaning rather than a mistake.
                   | "overkill"
                   // ⚠ BLEND joins the ENDS of two curves, so it carries picks like FILLET.
                   | "blend"
                   // ⚠ CHSPACE also acts across the two spaces, and SCALES by the viewport it
                   // moves through — see `edits.do_chspace`. It was refused for months with the
                   // design app's reason ("there is one space here"), which is true of
                   // `/opt/dsn` and not of a drawing with 971 layouts in this database.
                   | "chspace"
                   // ⚠ STYLE — the DXF text style. 972 of them are stored on this box across 57
                   // fonts, and the ribbon's "Text Style…" used to open the design app's Pen
                   // Styles palette, which cannot express a font file or a width factor.
                   | "textstyle"
                   // ⚠ The Properties palette's write path. Layer, colour, linetype,
                   // lineweight and — for text — the string itself.
                   | "setprop"
                   // ⚠ PEDIT is ONE op with a `mode`, the way AutoCAD's own sub-menu works —
                   // not nine commands with names AutoCAD does not have. `pjoin` is separate
                   // because it makes one object out of several and undoes differently.
                   | "pedit" | "pjoin" | "dimspace" | "leaderalign" | "leadercollect" | "clipcopy" | "clippaste"
                   // ⚠ DESIGNCENTER. NOT a flavour of `insert`: `insert` refuses a name this
                   // drawing has never heard of, which is right — this one carries the
                   // definition and its nested definitions in first, then places it.
                   | "importblock"
                   // ⚠ UFADE / ADJUST ▸ Monochrome / XCLIP. All three are ENTITY data — groups
                   // 282, 280 and 11/12 — so they go out in the DXF and every reader sees them.
                   // FRAME is NOT here: whether the outline is stroked on this screen is a view
                   // setting and belongs in `Toggles`.
                   | "underlay"
                   // ⚠ BREAK acts on ONE object — the two points can only be on one of them,
                   // which is why the server refuses a longer selection by name.
                   | "break"
                   // ⚠ BOUNDARY answers the same question as the internal-point hatch — "what
                   // encloses this point?" — into a polyline instead of a fill.
                   | "boundary" | "centremark" | "centreline" | "constrain" | "vpucs"
                   // ⚠ The MODEL-space UCS, which lives in the drawing's header — `vpucs` is its
                   // paper-space twin and writes the viewport instead. One op for three header
                   // variables, so one Ctrl+Z puts the origin back.
                   | "ucs"
                   // ⚠ CUTCLIP — takes the payload AND erases, in one undoable step. COPYCLIP's
                   // `clipcopy` is read-only; this one is not, which is the whole difference.
                   | "clipcut"
                   | "bedit"
                   // ⚠ SPLINEDIT is PEDIT's shape on a spline: one op, a `mode`, AutoCAD's own
                   // sub-menu. There is no `sjoin` — joining two splines is not a command
                   // AutoCAD has.
                   | "splinedit"
                   // ⚠ ARRAY creates and undoes by erasing what it made; STRETCH is the one
                   // modify command that transforms SOME of an entity's points rather than all
                   // of them — see `edits.do_stretch`.
                   | "array" | "stretch"
                   // ⚠ ATTEDIT changes attribute VALUES on the selected block references. It is
                   // not SETPROP: an ATTRIB is a SUB-ENTITY of the INSERT, carried inside its
                   // tag stream, so only the owning INSERT can change one.
                   | "attedit"
                   // ⚠ `dimstyle` writes the TABLE (no selection); `setdimstyle` writes the
                   // selected dimensions' group 3.
                   | "dimstyle" | "setdimstyle"
                   // ⚠ BIND, not attach. Attaching would store a path this app cannot read at
                   // plot time — see `do_bindxref`.
                   | "bindxref"
                   // ⚠ The rest of the DIMENSION family. `dimcont` and `dimbase` continue ONE
                   // existing dimension; `qdim` measures a whole selection at once.
                   | "dimcont" | "dimbase" | "qdim"
                   // ⚠⚠ **SETVAR IS AN EDIT, NOT A SETTING.** A system variable lives in the
                   // drawing's HEADER and travels with the file: `$LTSCALE` decides what every
                   // dashed line looks like in whoever opens it next. Storing it beside the app
                   // would make this app's rendering disagree with AutoCAD's on the same file.
                   | "setvar"
                   // ⚠⚠ **AutoCAD'S ACTUAL HATCH GESTURE** — click INSIDE the area and the
                   // boundary is traced from what encloses the point. `hatch` is the other half
                   // of the same prompt, "Select objects", and neither replaces the other.
                   | "hatchpoint"
                   // ⚠⚠ A GROUP is not a BLOCK — several objects that stay several, each
                   // keeping its own layer, colour and handle. See `edits.do_group`.
                   | "group" | "ungroup" | "groupedit"
                   // ⚠ ATTDEF defines what a block CAN say; ATTSYNC brings placed copies into
                   // step, keeping every value a drafter typed. Separate in AutoCAD too.
                   | "attdef" | "attsync";

export interface EditReport {
  /** VPLAYER / VPLOCK — how many WINDOWS were changed, not how many objects. */
  viewports?: number;
  /** VPLOCK's new state, echoed so the caller does not have to assume it took. */
  locked?: boolean;
  /** STYLE — how many pieces of text the change reaches. ⚠ Reported because "renamed the font
   *  on 1,946 labels" is a sentence a drafter should read, not discover. The style's own NAME
   *  comes back in `style`, which DIMSTYLE already declares below. */
  affects?: number;
  moved?: number;
  copied?: number;
  /** CHSPACE — where the objects went, and through which window.
   *  ⚠ `from` and `scale` are already declared below and are REUSED, the same way BIND reuses
   *  `block`: they are the same facts — a name it came from, a factor it was scaled by — and a
   *  second key for either could disagree with the first. */
  to?: string;
  through?: string;
  /** The cross-drawing clipboard. ⚠ `rows` and `blocks` are opaque here ON PURPOSE — they are
   *  `_freeze` records the server made and the server reads back, and giving the browser a typed
   *  shape for them would be a second declaration of the entity model that drifts from the
   *  first. The browser's only job is to hold them. */
  pasted?: number;
  /** PASTEBLOCK — `pasted` is 1 (the INSERT), `wrapped` is how many objects went INTO the block.
   *  ⚠ Both, because "1 object pasted" is a lie about a 300-line detail. The block's own name
   *  comes back in `block`, already declared below. */
  wrapped?: number;
  /** DESIGNCENTER — how many definitions travelled. ⚠ 0 is a REAL and different outcome: this
   *  drawing already had that block and it was KEPT, because redefining one would reshape every
   *  reference already on the plan. */
  /** GEOMCONSTRAINT / AUTOCONSTRAIN — how many objects the constraint actually MOVED.
   *  ⚠⚠ Not how many were selected, and the difference is the point: a constraint skips what
   *  already satisfies it and refuses what it cannot act on, so 0 and 4 are both ordinary
   *  answers to a selection of five. The ribbon printed the SELECTION size before this existed.
   */
  n?: number;
  /** BEDIT — the block name, per mode. ⚠ Three fields rather than one `name`, because which
   *  one comes back IS the answer: a caller that read `name` could not tell a save from a
   *  discard without also inspecting what it asked for. */
  opened?: string;
  saved?: string;
  cancelled?: string;
  /** BEDIT save — how many placed copies now draw the new definition. ⚠ Said out loud: editing
   *  a block changes every one of them, and that should be a number a drafter reads, not a
   *  thing they discover on another sheet. */
  instances?: number;
  objects?: number;
  /** BREAK — how many objects the one broken became. ⚠ 1 is a real outcome: breaking off the
   *  end of a line shortens it and makes nothing. */
  pieces?: number;
  broken?: number;
  blocks_carried?: number;
  already_here?: boolean;
  /** The underlay controls. ⚠ `fade` comes back 0–100, AutoCAD's own scale — the ribbon's 0–1
   *  is converted at the studio boundary and nowhere else. */
  fade?: number | null;
  mono?: boolean | null;
  clipped?: boolean | null;
  rows?: unknown[];
  blocks?: unknown[];
  base?: number[];
  erased?: number;
  offset?: number;
  drawn?: number;
  trimmed?: number;
  extended?: number;
  cornered?: number;
  //: ⚠ How many arcs CIRCLE's `turn Arc into circle` replaced. Its own name, because "cornered"
  //: and "trimmed" mean specific things and a shared counter would make the report ambiguous.
  converted?: number;
  //: ⚠ How many objects REVCLOUD's `Entity` traced. Its own name for the same reason
  //: `converted` has one: a shared counter makes the report ambiguous.
  clouded?: number;
  hatched?: number;
  changed?: number;
  pattern?: string;
  blocked?: number;
  /** Paper space. `window` is the size in paper millimetres, `scale` the 1:n denominator —
   *  ⚠ REPORTED rather than assumed, because MVIEW with no scale FITS and the drafter has to be
   *  told which standard scale that came out as. */
  layout?: string;
  window?: [number, number];
  scale?: number;
  scaled?: number;
  created?: number;
  deleted?: number;
  /** ⚠ Also SETVAR's previous value, which is a number as often as a string. */
  was?: string | number | null;
  /** SETVAR — the variable it wrote and what it now holds. */
  name?: string;
  value?: string | number;
  /** HATCHPOINT — the traced area, and how many islands were left unfilled inside it. */
  area?: number;
  holes?: number;
  /** GROUP / GROUPEDIT / ATTDEF / ATTSYNC. */
  grouped?: number;
  members?: number;
  synced?: number;
  added?: number;
  removed?: number;
  kept?: number;
  tag?: string;
  /** ⚠ SETPROP reports what it COULD NOT do as well as what it did: a selection of forty walls
   *  and one dimension changes the forty, and the drafter has to be told about the one.
   *  `refused_count` is already declared above — SETPROP reuses it deliberately, because a
   *  second name for the same fact is how two counts come to disagree. */
  properties?: string[];
  /** PEDIT / PJOIN. ⚠ `handles` on a JOIN is the polyline it MADE, and the studio re-selects
   *  it — the pieces it was made from no longer exist. */
  joined?: number;
  /** ARRAY reports how many objects it MADE — 3x4 of a six-object selection is thirty, not
   *  six and not twelve. ⚠ `items` is the grid count including the original, which is what the
   *  drafter typed; the two are different numbers and both are useful. */
  /** ATTEDIT. ⚠ `attributes` counts ATTRIBUTES and `changed` counts BLOCKS — three blocks each
   *  with one matching tag is `changed 3, attributes 3`, and one block with two matching tags is
   *  `changed 1, attributes 2`. Two different questions, both worth answering. */
  attributes?: number;
  without_the_tag?: number;
  arrayed?: number;
  items?: number;
  /** STRETCH. ⚠ `moved_whole` are the ones with no vertex to reach — a circle caught by the
   *  window moves rather than becoming an ellipse — and `untouched` are the ones the command
   *  cannot act on at all, said out loud rather than silently skipped. */
  stretched?: number;
  moved_whole?: number;
  untouched?: Record<string, number>;
  untouched_count?: number;
  vertices?: number;
  left_alone?: number;
  straightened?: number;
  mode?: string;
  style?: string;
  // ⚠ `created` is already declared above as a NUMBER (newlayout returns a count). A dimension
  // style reports a BOOLEAN — "did this make a new one or update an existing one" — so it gets
  // its own name rather than a widened type that means two things.
  made?: boolean;
  bound?: number;
  // ⚠ `block` is already declared above (BLOCK returns the name it made). BIND reuses it
  // deliberately — it is the same fact, a block's name — rather than adding a second key that
  // could disagree with the first.
  from?: string;
  /** ⚠ Printed verbatim. SETDIMSTYLE sets the style and does NOT re-render the `*D` block the
   *  drafter can see — a fact they have to be told rather than discover. */
  note?: string;
  inserted?: number;
  exploded?: number;
  block?: string;
  /** ⚠ True when a TRIM cut a piece out of the MIDDLE, leaving two objects. The drafter has to
   *  be told: they now own one more thing than they did. */
  split?: boolean;
  /** The kind an object BECAME. A trimmed CIRCLE is an ARC — a different object. */
  became?: string;
  /** ⚠ Why a command did nothing — see the existing `detail` below. "0 objects trimmed" is not
   *  actionable and "nothing crosses that object" is. */
  /** The DXF type the server actually made. ⚠ Not always what was asked for — a RECT comes back
   *  as an LWPOLYLINE, which is what a drafter should be told it now owns. */
  kind?: string;
  handles?: string[];
  /** ⚠ Kinds the server would not touch, by name. Never empty-and-silent: a MOVE that quietly
   *  left eight objects behind is the failure this whole app is built against. */
  refused?: Record<string, number>;
  refused_count?: number;
  links_dropped?: number;
  block_entities?: number;
  op?: string;
  detail?: string;
}

async function post(url: string, body?: unknown): Promise<EditReport> {
  const r = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json", ...csrf() },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (r.status === 401 || r.status === 403) throw new NotLoggedIn();
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j?.detail || `${r.status} ${r.statusText}`);
  return j as EditReport;
}

/** ⚠ `params`, not a `delta`. MOVE takes a vector, ROTATE a base point and an angle, MIRROR two
 *  points — widening one field to mean all of those is how an API grows a `delta` holding an
 *  angle. `views.edit` documents each command's keys. */
export interface AttDef {
  tag: string; prompt: string; default: string; height: number;
  constant: boolean; invisible: boolean;
}

/** BATTMAN — a block's attribute DEFINITIONS. ⚠ The definitions, not any instance's values: a
 *  definition exists before anything is placed, and its PROMPT is what says what the tag is
 *  for. Values arrive with the display list, in `atts`. */
export async function loadBlockAtts(id: number, block?: string):
    Promise<{ block?: string; attributes?: AttDef[]; blocks?: { block: string; attributes: AttDef[] }[] }> {
  const d = localFor(id);
  if (d) return await d.blockAtts(block || "") as never;
  const q = block ? `?block=${encodeURIComponent(block)}` : "";
  const r = await fetch(`/api/drawings/${id}/blockatts/${q}`, { credentials: "same-origin" });
  if (!r.ok) throw new Error(`blockatts ${r.status}`);
  return r.json();
}

/** One text entity's RAW content, for the in-place editor.
 *  ⚠⚠ RAW — the display list sends `plain_text()`, which strips every inline code, and 77% of
 *  this office's MTEXT carries at least one. See `views.text`. */
export async function loadText(id: number, handle: string): Promise<TextSpec> {
  const d = localFor(id);
  if (d) return await d.text(handle) as TextSpec;
  const r = await fetch(`/api/drawings/${id}/text/?h=${encodeURIComponent(handle)}`,
                        { credentials: "same-origin" });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail ?? `text ${r.status}`);
  return r.json();
}

/** One system variable, as the palette and SETVAR need it. */
export interface SysVar {
  name: string; value: unknown; default: unknown; set: boolean; reads: string;
  /** ⚠ `"drawing"` travels in the file; `"user"` is this drafter's own — see
   *  `sysvars.USER_SCOPED`. The palette says which, because "this is the client's setting" and
   *  "this is yours" are different promises. */
  where?: "drawing" | "user";
}

/** Every system variable this app HONOURS, and a count of the ones it does not.
 *  ⚠⚠ The count matters: a real job carries 251 header variables and pdcad reads 21. Listing
 *  all 251 would be a settings page where 230 fields do nothing — see `views.sysvars`. */
export async function loadSysvars(id: number):
    Promise<{ vars: SysVar[]; carried: number; mine?: Record<string, number> }> {
  const d = localFor(id);
  if (d) return await d.sysvars({}) as never;
  const r = await fetch(`/api/drawings/${id}/sysvars/`, { credentials: "same-origin" });
  if (!r.ok) throw new Error(`sysvars ${r.status}`);
  return r.json();
}

/** One group as the palette needs it. */
export interface GroupRow {
  handle: string; name: string; description: string;
  unnamed: boolean; selectable: boolean; members: string[]; missing: number;
}

/** Every GROUP in the drawing. ⚠⚠ A group is NOT a block — a block is ONE object with one
 *  insertion point, a group is several objects that stay several. See `views.groups`. */
export async function loadGroups(id: number): Promise<{ groups: GroupRow[] }> {
  const d = localFor(id);
  if (d) return await d.groups() as { groups: GroupRow[] };
  const r = await fetch(`/api/drawings/${id}/groups/`, { credentials: "same-origin" });
  if (!r.ok) throw new Error(`groups ${r.status}`);
  return r.json();
}

export function editDrawing(id: number, op: EditOp, handles: string[],
                            params: Record<string, unknown> = {}): Promise<EditReport> {
  // ⚠⚠⚠ **THE ONE MARK THAT MAKES THE BROWSER ENGINE MEASURABLE, AND IT HAS TO BE HERE.**
  // `qa/bench_actions.py` counted edits by wrapping `window.fetch` — which works on the server
  // path and finds NOTHING on the local one, because a local edit makes no request. Run against
  // a drawing taken LOCAL, the harness waited 45 s per action for a request that was never going
  // to come, and thirty-one actions took twenty-three minutes to report nothing at all.
  // ⇒ This is the choke point BOTH paths go through, so one mark here covers both and the two
  // arms stay comparable. It keeps company with `__pdcadOptimistic`, `__pdcadRefresh` and
  // `__pdcadScene`, which exist for the same reason: this repo has been wrong every time it
  // inferred a phase from outside instead of measuring it from within.
  // ⚠ Bounded — a long session must not grow a list nobody reads.
  const t0 = performance.now();
  // ⚠⚠ **SPLIT, BECAUSE THE TOTAL HAS BEEN WRONG TWICE.** A local draw measured 27-37 ms here
  // while the engine's own edit is 0.5 ms and its display patch 1.0 (`qa/local_adapter.py
  // --time`), and two guesses at where the rest went — the GPU scene, the autosave journal —
  // were both wrong. `mark` is set by the branch that knows.
  const split: Record<string, number> = {};
  return editDrawingOf(id, op, handles, params, split).then((rep) => {
    try {
      const w = window as unknown as Record<string, unknown>;
      type Mark = { op: string; ms: number; local: boolean; split?: Record<string, number> };
      const log = (w.__pdcadEdits ||= []) as Mark[];
      log.push({ op, ms: performance.now() - t0, local: localFor(id) != null, split });
      if (log.length > 256) log.splice(0, log.length - 256);
      // ⚠⚠ **A COUNTER THAT NEVER FORGETS, because the LIST does.** `__pdcadEdits` is capped at
      // 256 so a long session cannot grow it without bound — and a harness that counts edits by
      // the list's LENGTH reads zero once it saturates. That is not hypothetical: a 14-command
      // sweep of 20-command bursts reported COPY 18, SCALE 14 and MIRROR 0 while the transcript
      // said `1 object mirrored`; the cap had swallowed the difference, and three commands were
      // about to be recorded as broken.
      w.__pdcadEditCount = ((w.__pdcadEditCount as number) ?? 0) + 1;
    } catch { /* a mark must never be the reason an edit fails */ }
    return rep;
  });
}

function editDrawingOf(id: number, op: EditOp, handles: string[],
                       params: Record<string, unknown> = {},
                       split: Record<string, number> = {}): Promise<EditReport> {
  const d = localFor(id);
  // ⚠ The LOCAL answer is `ops::apply_json`'s document, and the studio wants the `report` —
  // the same object the endpoint puts under that key. A command the engine does not know
  // REJECTS, and the caller shows the refusal rather than quietly reaching the server for a
  // drawing it has never seen.
  if (d) {
    const t1 = performance.now();
    return d.edit(op, handles, params).then(async (a) => {
      split.engine = performance.now() - t1;
      // ⚠ the engine's own clock for each half, from `LocalDrawing.lastEdit`
      Object.assign(split, LocalDrawing.lastEdit || {});
      // ⚠⚠⚠ **AND THEN REDRAW ONLY WHAT CHANGED.** Without this the report carries no delta,
      // `Studio.refresh` finds none and refetches the whole display list — 716.8 ms on a
      // 57,385-object drawing for an edit the engine did in 5.8 (`qa/local_adapter.py --time`).
      // The server has answered with `_redraw._deltas` since the twenty-third pass; this is the
      // same shape, so `delta.ts` and `Studio.refresh` need to know nothing about where it came
      // from.
      // ⚠ The handles the EDIT touched, not the ones it was given: a command that creates
      // (ARRAY, EXPLODE) or deletes must redraw what it made, and `writes`/`creates`/`deletes`
      // are what the engine actually did.
      const touched = new Set<string>(handles.map((h) => h.toUpperCase()));
      for (const w of a.writes || []) touched.add(String(w.handle).toUpperCase());
      for (const c of a.creates || []) touched.add(String(c.handle).toUpperCase());
      for (const h of a.deletes || []) touched.add(String(h).toUpperCase());
      const rep = a.report as EditReport & {
        _redraw?: { _deltas?: Record<string, unknown> };
      };
      // ⚠ A refusal is `undefined` and the studio refetches, exactly as it does when the server
      // declines. Never a guess: this is the picture.
      const t2 = performance.now();
      const delta = await d.displayPatch([...touched]);
      split.redraw = performance.now() - t2;
      if (delta) rep._redraw = { _deltas: { [d.shownSpace()]: delta } };
      return rep;
    }) as Promise<EditReport>;
  }
  return post(`/api/drawings/${id}/edit/`, { op, handles, params });
}
/** IMAGEATTACH. ⚠⚠ **NO `Content-Type` HEADER — the browser must set it.** A multipart body
 *  needs a boundary parameter that only `FormData` knows; setting `multipart/form-data` by hand
 *  omits it and Django parses an empty request, which arrives as "no file" with the file
 *  plainly attached. */
export async function attachImage(id: number, body: FormData): Promise<EditReport & {
  image?: string; pixels?: number[]; def?: string;
}> {
  const r = await fetch(`/api/drawings/${id}/attach_image/`, {
    method: "POST", credentials: "same-origin", headers: { Accept: "application/json", ...csrf() },
    body,
  });
  const got = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(got?.detail || `attach ${r.status}`);
  return got;
}

/** PDFATTACH — same multipart shape as `attachImage`, plus the PAGE.
 *
 * ⚠⚠ **A REAL `PDFUNDERLAY` GOES INTO THE DXF, NOT THE PIXELS.** The client opens the drawing
 * against THEIR copy of the PDF, at full vector resolution; the page this app renders is a
 * local cache so it can be drawn here, and is never the deliverable. */
export async function attachPdf(id: number, body: FormData): Promise<EditReport & {
  underlay?: string; page?: number; pages?: number; pixels?: number[]; def?: string;
  size?: number[]; size_in?: number[];
}> {
  const r = await fetch(`/api/drawings/${id}/attach_pdf/`, {
    method: "POST", credentials: "same-origin", headers: { Accept: "application/json", ...csrf() },
    body,
  });
  const got = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(got?.detail || `attach ${r.status}`);
  return got;
}

/** The pages in a PDF, BEFORE anything is placed.
 *
 * ⚠⚠ **PDFATTACH USED TO ALWAYS PLACE PAGE 1.** A consultant's tender set is 12 pages and the
 * one being traced is rarely the cover. This probe stores nothing and creates nothing — it is
 * about a FILE, not about a drawing, which is why it is not under `/drawings/<id>/`. */
export interface PdfPages {
  pages: number; truncated?: boolean;
  sizes: { page: number; w_in: number; h_in: number; w_mm: number; h_mm: number }[];
}
export async function pdfPages(body: FormData): Promise<PdfPages> {
  const r = await fetch("/api/drawings/pdf_pages/", {
    method: "POST", credentials: "same-origin", headers: { Accept: "application/json", ...csrf() },
    body,
  });
  const got = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(got?.detail || `pdf_pages ${r.status}`);
  return got;
}

/** AREA / DIST / MEASUREGEOM — a question, not a change.
 *
 * ⚠⚠ **NOT `editDrawing`, AND THAT IS THE WHOLE REASON IT IS SEPARATE.** Every edit op pushes an
 * undo record. Measuring changes nothing, and routing it through `/edit/` would put "measured a
 * room" on the undo stack between two real edits — so Ctrl+Z after checking an area would undo
 * the measurement and leave the drafter's actual last change standing. */
export interface Measured {
  kind: string;
  area?: number; perimeter?: number; objects?: number; picked?: number;
  open?: number; closed?: boolean; refused?: Record<string, number>;
  distance?: number; dx?: number; dy?: number; angle?: number; reflex?: number;
  radius?: number; diameter?: number; circumference?: number; entity?: string;
}
export async function measure(id: number, handles: string[],
                              spec: Record<string, unknown>): Promise<Measured> {
  // ⚠ Its own fetch rather than `post`, which is typed to `EditReport`. A measurement is not an
  // edit report and pretending it is would put `moved`/`erased` on a thing that changes nothing.
  const r = await fetch(`/api/drawings/${id}/measure/`, {
    method: "POST", credentials: "same-origin",
    headers: { "Content-Type": "application/json", Accept: "application/json", ...csrf() },
    body: JSON.stringify({ handles, ...spec }),
  });
  const got = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(got?.detail || `measure ${r.status}`);
  return got;
}

/** The Parameters Manager, DIMCONSTRAINT, CONSTRAINTBAR and DELCONSTRAINT — one route.
 *
 * ⚠ Its own fetch rather than `post`, for `measure`'s reason: the answer is the whole parametric
 * STATE — rows, values, what the geometry now reads and what failed — not an `EditReport`.
 * ⚠ GET / POST / DELETE all return that same state, because the dialog needs all of it after
 * every one of them. See `backend/drawing/parametric.py` for how much of AutoCAD's parametrics
 * this is, and how much it deliberately is not.
 */
export async function parametric(id: number, method: "GET" | "POST" | "DELETE" = "GET",
                                 body?: unknown, query = ""): Promise<any> {
  const r = await fetch(`/api/drawings/${id}/parametric/${query ? `?${query}` : ""}`, {
    method, credentials: "same-origin",
    headers: { "Content-Type": "application/json", Accept: "application/json", ...csrf() },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const got = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(got?.detail || `parametric ${r.status}`);
  return got;
}

/** What EXPORTLAYOUT made. ⚠⚠ **ITS OWN TYPE, NOT A WIDENED `EditReport`.** Every other call
 *  here reports on an edit to the CURRENT drawing; this one reports a second drawing that now
 *  exists. Adding five fields to `EditReport` would make them optional on every op that can
 *  never return them, which is how a report type stops describing anything. */
export type ExportedLayout = {
  drawing: number; name: string; from: string; scale: number; through: string;
  model_objects: number; sheet_objects: number;
  outside_the_viewport: number; kept_whole_across_the_border: number;
  not_transformable: number;
};

/** EXPORTLAYOUT — this layout as a NEW drawing, the sheet flattened into model space.
 *
 * ⚠ Not an `editDrawing` op. It does not change THIS drawing, so it has no inverse and belongs
 * on no undo stack — see the endpoint's own docstring. */
export const exportLayout = (id: number, space?: string, name?: string) =>
  post(`/api/drawings/${id}/exportlayout/`,
       { ...(space ? { space } : {}), ...(name ? { name } : {}) }) as Promise<ExportedLayout>;

/** ⚠⚠ **LOCAL MODE MUST ANSWER THE SAME SHAPE, INCLUDING WHEN THERE IS NOTHING TO UNDO.** The
 *  endpoint returns AutoCAD's own wording and a 200 — pressing Ctrl+Z once too often is not a
 *  failure — and `Studio.tsx` reads `detail`, `op` and the delta out of whatever comes back. A
 *  different shape here would be a second thing the studio has to know about undo. */
export const undoDrawing = (id: number) => step(id, "undo");
export const redoDrawing = (id: number) => step(id, "redo");

/** UNDO and REDO, both modes. ⚠ Marked like an edit (`__pdcadEdits`) because they ARE one to the
 *  drafter and to anything measuring: `qa/bench_actions.py --local` read UNDO as "NO EDIT" purely
 *  because undo does not go through `editDrawing`. */
function step(id: number, which: "undo" | "redo") {
  const t0 = performance.now();
  const d = localFor(id);
  // ⚠⚠⚠ **AN UNDO CARRIES A DELTA NOW, AND WITHOUT ONE IT COST 247 ms A PRESS.** `undo` returned
  // a bare `true`, so `Studio.refresh` found no delta and refetched the whole display list. In
  // local mode that comes back from the engine as entirely fresh rows, which throws away
  // `gpu/build.ts`'s fill cache and re-triangulates every fill: measured on drawing 1158,
  // **scene build 309-376 ms, `fills` 243-247 of it** — on the key a drafter presses most, and
  // for an operation that touched forty-nine objects. Every other command has carried its
  // touched handles since `editDrawingOf`; this is the same two lines.
  // ⚠ A redo goes through `edit`, which leaves its own `lastDelta`; `displayPatch` reads that
  // first and the handles below are never used. Both paths are covered by the same call.
  const withDelta = (op: "undo" | "redo", detail: string, none: string) =>
    (touched: string[] | null) => {
      if (!touched) return Promise.resolve({ detail: none });
      return Promise.resolve(d!.displayPatch(touched))
        .catch(() => undefined)
        .then((delta) => {
          const rep: Record<string, unknown> = { op, detail };
          if (delta) rep._redraw = { _deltas: { [d!.shownSpace()]: delta } };
          return rep;
        });
    };
  const go = d
    ? (which === "undo"
        ? d.undo().then(withDelta("undo", "UNDO", "Everything has been undone"))
        : d.redo().then(withDelta("redo", "REDO", "Nothing to redo")))
    : post(`/api/drawings/${id}/${which}/`);
  return Promise.resolve(go).then((rep) => {
    try {
      const w = window as unknown as Record<string, unknown>;
      const log = (w.__pdcadEdits ||= []) as { op: string; ms: number; local: boolean }[];
      log.push({ op: which, ms: performance.now() - t0, local: d != null });
      if (log.length > 256) log.splice(0, log.length - 256);
    } catch { /* a mark must never be the reason an undo fails */ }
    return rep;
  });
}

// ── who is signed in ─────────────────────────────────────────────────────────────────────────

export interface Who { id: number; username: string; name: string; staff: boolean }

/** ⚠ Also what puts the CSRF cookie in place — `auth.Me` is `ensure_csrf_cookie`. Call it first,
 *  always, or the first POST of the session is a 403 nobody can explain. */
export async function whoAmI(): Promise<Who | null> {
  const r = await fetch("/api/auth/me/", { headers: { Accept: "application/json" } });
  if (!r.ok) return null;
  return (await r.json()).user as Who | null;
}

export async function signIn(username: string, password: string): Promise<Who> {
  const r = await fetch("/api/auth/login/", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json", ...csrf() },
    body: JSON.stringify({ username, password }),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j?.detail || `${r.status} ${r.statusText}`);
  return j.user as Who;
}

export async function signOut(): Promise<void> {
  await fetch("/api/auth/logout/", { method: "POST", headers: { ...csrf() } });
}

/** One grip on one object. ⚠ `role` is what DRAGGING it means, not a label: a circle's four
 *  quadrant grips change its RADIUS while its centre grip moves it. */
export interface Grip { p: [number, number]; role: string }

export async function loadGrips(id: number, handle: string): Promise<Grip[]> {
  const j = await get<{ grips: Grip[] }>(
    `/api/drawings/${id}/grips/?handle=${encodeURIComponent(handle)}`);
  return j.grips ?? [];
}

/** ⚠ From the SERVER, which reads ezdxf's own table — see `views.HatchPatterns`. A hard-coded
 *  list in the browser is how a picker comes to offer a pattern the writer cannot write. */
export function loadHatchPatterns(): Promise<{ patterns: string[]; common: string[] }> {
  return get<{ patterns: string[]; common: string[] }>("/api/hatch-patterns/");
}

// ── managing the drawings themselves ─────────────────────────────────────────────────────────

async function send(url: string, method: string, body?: unknown): Promise<any> {
  const r = await fetch(url, {
    method,
    headers: { "Content-Type": "application/json", Accept: "application/json", ...csrf() },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (r.status === 401 || r.status === 403) {
    // ⚠ 403 here is not always a login problem — `purge` refuses a non-administrator with one.
    // The server's sentence is the only thing that can tell them apart.
    const j = await r.json().catch(() => ({}));
    if (j?.detail) throw new Error(j.detail);
    throw new NotLoggedIn();
  }
  if (r.status === 204) return {};
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j?.detail || `${r.status} ${r.statusText}`);
  return j;
}

/** ⚠ Only the NAME is writable — see `serializers.DrawingSerializer`. */
export const renameDrawing = (id: number, name: string) =>
  send(`/api/drawings/${id}/`, "PATCH", { name }) as Promise<DrawingRow>;

/** ⚠⚠ ARCHIVES. It does not destroy — see `views.DrawingViewSet`. */
export const archiveDrawing = (id: number) => send(`/api/drawings/${id}/`, "DELETE");
export const restoreDrawing = (id: number) => send(`/api/drawings/${id}/restore/`, "POST");
export const purgeDrawing = (id: number) => send(`/api/drawings/${id}/purge/`, "POST");

export async function duplicateDrawing(id: number): Promise<DrawingRow> {
  const j = await send(`/api/drawings/${id}/duplicate/`, "POST");
  return j.drawing as DrawingRow;
}

export function listDrawingsIncludingArchived(): Promise<DrawingRow[]> {
  return listDrawings("?archived=1");
}

/** A block definition in one drawing. ⚠ `used` is what makes the list actionable: a block used
 *  40 times is the office's standard door and one used 0 times is debris. */
export interface BlockRow { name: string; entities: number; used: number }

export const loadBlocks = (id: number) => {
  const d = localFor(id);
  if (d) return d.blocks() as Promise<BlockRow[]>;
  return get<BlockRow[]>(`/api/drawings/${id}/blocks/`);
};

/** DESIGNCENTER — one block definition, frozen, with every block IT names.
 *
 * ⚠⚠ **`blocks` IS OPAQUE HERE ON PURPOSE**, exactly as the clipboard's rows are: it is what
 * the server froze and what the server reads back, and a typed shape for it in the browser
 * would be a second declaration of the entity model that drifts from the first. */
export interface BlockDefPayload {
  name: string; blocks: unknown[]; entities: number; nested: number;
  refused?: Record<string, number>;
}
export const loadBlockDef = (id: number, name: string) => {
  const d = localFor(id);
  if (d) return d.blockDef(name) as Promise<BlockDefPayload>;
  return get<BlockDefPayload>(`/api/drawings/${id}/block_def/?name=${encodeURIComponent(name)}`);
};
